import useNftStats from './useNftStats';
import './index.scss';

// ---------------------------------------------------------------------------
// Every figure on this page is read live from the BSC contracts and the
// marketplace subgraph. Nothing is hardcoded and nothing is estimated, so the
// labels have to stay honest about what each number actually counts — two
// currencies are reported separately rather than blended, and "holders" means
// distinct wallets, not tokens minted.
// ---------------------------------------------------------------------------

const nf = new Intl.NumberFormat('en-US');
const compact = (n) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 1 : 2)}M`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`
      : nf.format(Math.round(n));

const Stat = ({ value, label, sub, wide }) => (
  <div className={`mcstats__tile${wide ? ' mcstats__tile--wide' : ''}`}>
    <div className="mcstats__tile-value">{value}</div>
    <div className="mcstats__tile-label">{label}</div>
    {sub && <div className="mcstats__tile-sub">{sub}</div>}
  </div>
);

// Cumulative sales, drawn as two stacked areas. Colours are the validated
// categorical pair (#35a9a2 / #6d52dd) — the brand cyan itself sits outside the
// legible lightness band on this ground, so it is reserved for text and glow.
const GENESIS_HUE = '#35a9a2';
const REVELATION_HUE = '#6d52dd';

const Chart = ({ points }) => {
  if (!points.length) return null;

  const W = 1000;
  const H = 260;
  const PAD = { l: 8, r: 8, t: 14, b: 26 };
  const max = Math.max(...points.map((p) => p.total)) || 1;
  const x = (i) =>
    PAD.l + (i / Math.max(1, points.length - 1)) * (W - PAD.l - PAD.r);
  const y = (v) => PAD.t + (1 - v / max) * (H - PAD.t - PAD.b);

  const area = (key) => {
    const top = points.map((p, i) => `${x(i)},${y(p[key])}`).join(' L');
    return `M${x(0)},${y(0)} L${top} L${x(points.length - 1)},${y(0)} Z`;
  };
  const line = (key) =>
    `M${points.map((p, i) => `${x(i)},${y(p[key])}`).join(' L')}`;

  const ticks = points.filter(
    (p, i) => i === 0 || i === points.length - 1 || p.month.endsWith('-01'),
  );

  return (
    <figure className="mcstats__chart">
      <figcaption className="mcstats__chart-head">
        <h3>Cumulative NFTs sold</h3>
        <div className="mcstats__legend">
          <span>
            <i style={{ background: GENESIS_HUE }} /> Genesis
          </span>
          <span>
            <i style={{ background: REVELATION_HUE }} /> Revelation
          </span>
        </div>
      </figcaption>

      <div className="mcstats__chart-plot">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
          aria-label={`Cumulative NFTs sold, rising to ${points[points.length - 1].total} by ${points[points.length - 1].month}`}>
          <defs>
            <linearGradient id="mcGen" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={GENESIS_HUE} stopOpacity="0.45" />
              <stop offset="100%" stopColor={GENESIS_HUE} stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="mcRev" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={REVELATION_HUE} stopOpacity="0.5" />
              <stop offset="100%" stopColor={REVELATION_HUE} stopOpacity="0.02" />
            </linearGradient>
          </defs>

          <path d={area('total')} fill="url(#mcGen)" />
          <path d={line('total')} fill="none" stroke={GENESIS_HUE} strokeWidth="2"
            vectorEffect="non-scaling-stroke" />
          <path d={area('revelation')} fill="url(#mcRev)" />
          <path d={line('revelation')} fill="none" stroke={REVELATION_HUE} strokeWidth="2"
            vectorEffect="non-scaling-stroke" />
        </svg>
      </div>

      <div className="mcstats__chart-axis">
        {ticks.map((t) => (
          <span key={t.month}>{t.month.slice(0, 4)}</span>
        ))}
      </div>
    </figure>
  );
};

const Stats = () => {
  const s = useNftStats();

  const minted = s.mintedGenesis + s.mintedRevelation;
  const maxSupply = s.maxGenesis + s.maxRevelation;
  const since = s.firstSaleDate
    ? s.firstSaleDate.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      })
    : '—';
  const years = s.firstSaleDate
    ? Math.max(1, Math.round((Date.now() - s.firstSaleDate.getTime()) / 3.156e10))
    : 0;

  if (s.loading) {
    return (
      <div className="mcstats mcstats--loading">
        <div className="mcstats__spinner" />
        <p>Reading the chain…</p>
      </div>
    );
  }

  return (
    <div className="mcstats">
      <header className="mcstats__hero">
        <p className="mcstats__eyebrow">Live from BSC · updated every visit</p>
        <h1>The MagicCraft NFT economy</h1>
        <p className="mcstats__lede">
          Every number here is read directly from the token contracts and the
          marketplace subgraph at the moment you load the page. Nothing is
          cached, curated, or rounded up.
        </p>
      </header>

      <section className="mcstats__headline">
        <Stat
          wide
          value={compact(s.mcrtVolume)}
          label="MCRT traded"
          sub={`across ${nf.format(s.totalSales)} sales`}
        />
        <Stat
          wide
          value={`${s.bnbVolume.toFixed(1)}`}
          label="BNB traded"
          sub="alongside the MCRT volume"
        />
        <Stat
          wide
          value={nf.format(minted)}
          label="NFTs minted"
          sub={maxSupply ? `of ${nf.format(maxSupply)} max supply` : null}
        />
      </section>

      <section className="mcstats__grid">
        <Stat
          value={nf.format(s.holdersGenesis + s.holdersRevelation)}
          label="Wallets holding"
          sub={`${nf.format(s.holdersGenesis)} Genesis · ${nf.format(s.holdersRevelation)} Revelation`}
        />
        <Stat value={nf.format(s.totalSales)} label="All-time sales" sub={`since ${since}`} />
        <Stat
          value={nf.format(s.uniqueTraders)}
          label="Unique traders"
          sub={`${nf.format(s.buyers)} buyers · ${nf.format(s.sellers)} sellers`}
        />
        <Stat
          value={nf.format(s.activeListings)}
          label="Listed right now"
          sub={`from ${nf.format(s.activeSellers)} sellers`}
        />
        <Stat
          value={nf.format(s.nftsTraded)}
          label="Distinct NFTs traded"
          sub={`most-traded changed hands ${s.mostTraded}×`}
        />
        <Stat value={`${years} yrs`} label="Marketplace uptime" sub={`trading since ${since}`} />
      </section>

      <Chart points={s.cumulative} />

      <section className="mcstats__split">
        <article className="mcstats__collection">
          <h3>Genesis</h3>
          <div className="mcstats__bar">
            <span
              style={{
                width: `${s.maxGenesis ? (s.mintedGenesis / s.maxGenesis) * 100 : 0}%`,
                background: GENESIS_HUE,
              }}
            />
          </div>
          <dl>
            <div><dt>Minted</dt><dd>{nf.format(s.mintedGenesis)} / {nf.format(s.maxGenesis)}</dd></div>
            <div><dt>Holders</dt><dd>{nf.format(s.holdersGenesis)}</dd></div>
            <div><dt>Listed now</dt><dd>{nf.format(s.listingsGenesis)}</dd></div>
          </dl>
        </article>

        <article className="mcstats__collection">
          <h3>Revelation</h3>
          <div className="mcstats__bar">
            <span
              style={{
                width: `${s.maxRevelation ? (s.mintedRevelation / s.maxRevelation) * 100 : 0}%`,
                background: REVELATION_HUE,
              }}
            />
          </div>
          <dl>
            <div><dt>Minted</dt><dd>{nf.format(s.mintedRevelation)} / {nf.format(s.maxRevelation)}</dd></div>
            <div><dt>Holders</dt><dd>{nf.format(s.holdersRevelation)}</dd></div>
            <div><dt>Listed now</dt><dd>{nf.format(s.listingsRevelation)}</dd></div>
          </dl>
        </article>

        <article className="mcstats__collection mcstats__collection--records">
          <h3>Records</h3>
          <dl>
            <div><dt>Largest MCRT sale</dt><dd>{compact(s.biggestMcrtSale)} MCRT</dd></div>
            <div><dt>Largest BNB sale</dt><dd>{s.biggestBnbSale.toFixed(2)} BNB</dd></div>
            <div><dt>Most-traded NFT</dt><dd>{s.mostTraded} sales</dd></div>
          </dl>
        </article>
      </section>

      <footer className="mcstats__note">
        MCRT and BNB volumes are reported separately because the two are
        denominated differently on-chain; combining them would invent a number
        that does not exist. Holder counts are distinct wallets per collection —
        a wallet holding both is counted in each.
      </footer>
    </div>
  );
};

export default Stats;
